import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, ArrowDown, ArrowUp } from "lucide-react";

/**
 * InsightsConfigEditor — form-based editor for the `insights_config` KV.
 * Currently exposes only the public listing sort order, but designed to
 * grow with more insight-page settings without adding new KVs.
 */
const DEFAULTS = {
  sort: "newest", // "newest" | "oldest"
};

export default function InsightsConfigEditor() {
  const [value, setValue] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/kv/insights_config")
      .then(({ data }) => setValue({ ...DEFAULTS, ...(data || {}) }))
      .catch(() => setValue(DEFAULTS))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/kv/insights_config", { value });
      toast.success("Configuração salva.");
    } catch (e) {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const setSort = (v) => setValue((s) => ({ ...s, sort: v }));

  if (loading) return <div className="text-sm text-neutral-500">Carregando…</div>;

  return (
    <div data-testid="insights-config-editor">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="font-display text-4xl">Config Insights</h1>
          <p className="text-sm text-neutral-500 mt-1">Comportamento da página pública <code>/insights</code>.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="insights-config-save"
          className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50"
        >
          <Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}
        </button>
      </div>

      <div className="border border-black/10 rounded-md p-5 max-w-2xl">
        <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">Ordenação dos artigos</div>
        <p className="text-xs text-neutral-500 mb-4">
          Define a ordem em que os artigos aparecem no <code>/insights</code>. Ordena por <b>ano</b>, com a data de criação como desempate.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setSort("newest")}
            data-testid="insights-sort-newest"
            className={`text-left border-2 rounded-md p-4 transition ${value.sort === "newest" ? "border-accent-orange bg-orange-50" : "border-black/10 hover:border-black/30"}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <ArrowDown className="w-4 h-4 text-accent-orange" />
              <span className="text-sm font-semibold">Mais novo → mais velho</span>
              {value.sort === "newest" && <span className="ml-auto text-[10px] uppercase bg-accent-orange text-white px-2 py-0.5 rounded-full">Ativo</span>}
            </div>
            <div className="text-xs text-neutral-500">Padrão de blog: o conteúdo mais recente aparece primeiro.</div>
          </button>

          <button
            type="button"
            onClick={() => setSort("oldest")}
            data-testid="insights-sort-oldest"
            className={`text-left border-2 rounded-md p-4 transition ${value.sort === "oldest" ? "border-accent-orange bg-orange-50" : "border-black/10 hover:border-black/30"}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <ArrowUp className="w-4 h-4 text-accent-orange" />
              <span className="text-sm font-semibold">Mais velho → mais novo</span>
              {value.sort === "oldest" && <span className="ml-auto text-[10px] uppercase bg-accent-orange text-white px-2 py-0.5 rounded-full">Ativo</span>}
            </div>
            <div className="text-xs text-neutral-500">Cronologia crescente: útil para trilhas de leitura sequenciais.</div>
          </button>
        </div>
      </div>
    </div>
  );
}
